import React from "react";
import { TouchableOpacity } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { LucideIcon } from "lucide-react-native";

import { useMedicalHistoryTheme } from "../theme/MedicalHistoryTheme";
import { METALLIC_PROGRESS_COLORS } from "./medicalHistoryVisualTokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { cardSurface } from "@louise/moon-ui";

export type RectangularOptionButtonProps = {
  icon: LucideIcon;
  label: string;
  isSelected: boolean;
  onPress: () => void;
  iconSize?: number;
  /** Stable analytics identifier for the question this option answers. */
  action?: string;
};

/**
 * Full-width rectangular option button — soft glass style matching onboarding cards.
 * Used for options that span the full width.
 */
export function RectangularOptionButton({
  icon: Icon,
  label,
  isSelected,
  onPress,
  iconSize = 20,
  action,
}: RectangularOptionButtonProps) {
  const capturePress = usePressCapture("RectangularOptionButton");
  const theme = useMedicalHistoryTheme();

  const optionCard = (
    <TouchableOpacity
      onPress={() => {
        capturePress(pressLabel(label), action);
        onPress();
      }}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      aria-pressed={isSelected}
      style={[
        {
          ...cardSurface(),
          width: "100%",
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          paddingHorizontal: 18,
          paddingVertical: 16,
        },
        isSelected && {
          borderWidth: 0,
          borderRadius: 14,
          backgroundColor: theme.optionSelectedSurface,
        },
      ]}
    >
      <Icon
        size={iconSize}
        color={isSelected ? theme.text : theme.textFaint}
        strokeWidth={isSelected ? 2 : 1.5}
      />
      <Text
        className="font-dm-sans-medium"
        style={{
          flex: 1,
          fontSize: 14,
          fontWeight: "500",
          lineHeight: 20,
          color: theme.text,
        }}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );

  return isSelected ? (
    <LinearGradient
      colors={METALLIC_PROGRESS_COLORS}
      locations={[0, 0.24, 0.5, 0.74, 1]}
      start={{ x: 0, y: 0.5 }}
      end={{ x: 1, y: 0.5 }}
      style={{ width: "100%", borderRadius: 16, padding: 2 }}
    >
      {optionCard}
    </LinearGradient>
  ) : (
    optionCard
  );
}
